import { useCallback, useEffect, useRef, useState } from "react";
import { useCall } from "@/call/CallProvider";
import { CallView } from "./CallView";
import { InkedPhoneSmall } from "./ink";

/**
 * The call pill, once the call on the page has scrolled away, and the panel it
 * opens into.
 *
 * A call already running on the page opens the panel on its own when it
 * scrolls away, so End call is never out of reach.
 */
export function FloatingCall({
  callRef,
  opened,
  setOpened,
}: {
  callRef: React.RefObject<HTMLElement | null>;
  opened: boolean;
  setOpened: (v: boolean) => void;
}) {
  const { state, load, hangUp } = useCall();
  const panel = useRef<HTMLDivElement>(null);
  const [away, setAway] = useState(false);

  useEffect(() => {
    const layout = () => {
      const call = callRef.current;
      setAway(call ? call.getBoundingClientRect().bottom < 200 : false);
    };
    layout();
    window.addEventListener("scroll", layout, { passive: true });
    window.addEventListener("resize", layout);
    return () => {
      window.removeEventListener("scroll", layout);
      window.removeEventListener("resize", layout);
    };
  }, [callRef]);

  const running = state.phase === "connecting" || state.phase === "live";
  const open = opened || (running && away);

  const openPanel = useCallback(() => {
    setOpened(true);
    load();
  }, [load, setOpened]);

  return (
    <div className="fl">
      <button
        className={`float${away && !open ? " show" : ""}`}
        type="button"
        aria-expanded={open}
        aria-controls="fpanel"
        onClick={(e) => {
          openPanel();
          // From the keyboard, focus goes into the panel with it.
          if (e.detail === 0) panel.current?.querySelector<HTMLElement>(".call")?.focus();
        }}
      >
        <InkedPhoneSmall />
        Talk to our AI
      </button>

      <div
        className="fpanel"
        id="fpanel"
        ref={panel}
        role="region"
        aria-label="Talk to our AI receptionist"
        hidden={!open}
      >
        <button
          className="close"
          type="button"
          aria-label="Close"
          onClick={() => {
            setOpened(false);
            if (running) hangUp();
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
        <CallView view="float" />
      </div>
    </div>
  );
}
